/** Build platform-chat-style assistant messages for workflow planner replies. */

import { inferCompanyActionsFromText } from "../ai/components/NexusFlowQuestionPanel";
import { buildReminderRecipientArtifact } from "./reminderRecipientArtifacts";

function workflowActionsFromPlan(data) {
  const actions = [];
  const companies = data?.company_options || data?.draft?.suggested_companies || [];
  companies.forEach((name) => {
    const label = String(name || "").trim();
    if (!label) return;
    actions.push({
      type: "select_company",
      label,
      value: label,
      reason: "Workspace company",
    });
  });

  const roles = data?.role_options || [];
  roles.forEach((role) => {
    const label = String(role || "").trim();
    if (!label) return;
    actions.push({
      type: "select_role",
      label,
      value: label,
      reason: "Participant role",
    });
  });

  if (data?.action === "confirm_send" || data?.action === "send_invites") {
    if (data?.role_explicit && data?.plan) {
      actions.push({
        type: "confirm",
        label: "Send invite email",
        value: "confirm",
        reason: "Create participant and send email",
      });
    }
  }

  const resendAwaitingConfirm =
    data?.draft?.awaiting === "confirm" &&
    (data?.flow_kind === "resend_opened_incomplete" ||
      data?.draft?.flow_kind === "resend_opened_incomplete");

  if (data?.action === "clarify" && resendAwaitingConfirm) {
    actions.push({
      type: "confirm",
      label: "Send reminder emails",
      value: "confirm",
      reason: "Confirm before sending resend templates",
    });
  }

  if (data?.action === "open_report" && data?.document) {
    const label = data.document.label || "report";
    actions.push({
      type: "confirm",
      label: `Open ${label}`,
      value: "open_report",
      reason: "Open PDF in a new tab",
      document: data.document,
    });
  }

  return actions;
}

export function buildWorkflowAssistantThreadMessage(data) {
  const reply = String(data?.reply || "").trim();
  if (!reply && data?.action !== "open_report") return null;

  let actions = workflowActionsFromPlan(data);
  const text =
    reply ||
    (data?.action === "open_report"
      ? `Ready to open ${data?.document?.label || "report"}.`
      : data?.action === "clarify"
        ? "I need a bit more detail to continue this workflow."
        : "");
  if (!actions.length && text) {
    actions = inferCompanyActionsFromText(text);
  }

  const reminderTable = buildReminderRecipientArtifact(data);
  const artifacts = reminderTable ? [reminderTable] : [];

  return {
    id: `workflow-reply-${Date.now()}`,
    role: "assistant",
    content: text,
    structured: {
      summary: text,
      sections: [],
      artifacts,
      actions,
      citations: [],
      related_pages: [],
      confidence: "high",
      answer_kind: data?.action === "clarify" ? "workflow_clarify" : data?.action || "workflow",
      workflow: {
        action: data?.action,
        flow_kind: data?.flow_kind || data?.draft?.flow_kind || "",
        flow_id: data?.flow_id || null,
      },
    },
    meta: {
      kind: "workflow_reply",
      workflowAction: data?.action || "",
      flowKind: data?.flow_kind || data?.draft?.flow_kind || "",
    },
  };
}

export function threadHasWorkflowPrompt(messages = []) {
  return (messages || []).some((m) => m?.meta?.kind === "workflow_start");
}
